// Offline outbox: when a request fails because of bad signal (no response at all),
// it's persisted here — IndexedDB, since queued photos/videos are multi-MB and would
// blow localStorage's ~5MB cap — and replayed in order the moment connectivity returns.
// Nothing the crew taps in the field is ever lost to a dead zone.
import { api, uploadMedia } from "./api";

// slow: the connection works (other requests answered meanwhile) but the server timed out on one
const state = { online: navigator.onLine, slow: false, pending: 0, draining: false, authBlocked: false };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn({ ...state }));
export const subscribeSync = (fn) => {
  listeners.add(fn);
  fn({ ...state });
  return () => listeners.delete(fn);
};

// ---------- IndexedDB ----------
const DB_NAME = "shs-offline";
const STORE = "outbox";

const openDb = () =>
  new Promise((res, rej) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: "id" });
    req.onsuccess = () => res(req.result);
    req.onerror = () => rej(req.error);
  });

const txDone = (tx) => new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = () => rej(tx.error); });

const idbPut = async (entry) => {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put(entry);
  await txDone(tx);
};
const idbDel = async (id) => {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(id);
  await txDone(tx);
};
const idbAll = async () => {
  const db = await openDb();
  return new Promise((res, rej) => {
    const req = db.transaction(STORE, "readonly").objectStore(STORE).getAll();
    req.onsuccess = () => res(req.result || []);
    req.onerror = () => rej(req.error);
  });
};

// ---------- classification ----------
// Only true connectivity failures (no response received) are queueable —
// a 4xx/5xx means the server HEARD us and refused, which retrying won't fix.
export const isNetworkError = (err) => !err?.response;
// the phone already knows there's no signal → skip the doomed request and save straight to the queue
export const isOffline = () => !navigator.onLine || !state.online;

// "a clock action is waiting on this phone" flags — cleared here the moment that entry syncs or
// is refused, whichever page is open, so a stale flag can never block the next clock-out
export const PENDING_CLOCKOUT_KEY = "shs-pending-clockout";
export const PENDING_CLOCKIN_KEY = "shs-pending-clockin";
const clearClockFlag = (entry) => {
  if (entry.kind === "clock-out") localStorage.removeItem(PENDING_CLOCKOUT_KEY);
  if (entry.kind === "clock-in") localStorage.removeItem(PENDING_CLOCKIN_KEY);
};
export const hasQueued = async (kind) => {
  try { return (await idbAll()).some((e) => e.kind === kind); } catch { return false; }
};

// No answer at all: a timeout while another request answered after this one started means the
// signal is fine and the server is just slow; anything else is treated as no signal.
let lastOkAt = 0;
let slowSince = 0;
const SLOW_MIN_MS = 8000; // keep the notice up long enough to read; the 10s check then clears it
const isTimeout = (err) => err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT";
const markNoResponse = (err) => {
  const startedAt = err?.config?.__startedAt || 0;
  if (navigator.onLine && isTimeout(err) && lastOkAt >= startedAt && startedAt > 0) {
    slowSince = Date.now();
    if (!state.slow) { state.slow = true; emit(); }
    pingSoon();
  } else if (state.online) {
    state.online = false;
    emit();
  }
};

// While the server is slow, check every 10s if it answers again (any answer clears the notice).
let pingTimer = null;
const pingSoon = () => {
  if (pingTimer) return;
  pingTimer = setTimeout(() => {
    pingTimer = null;
    if (state.slow) api.get("/", { timeout: 10000, __ping: true }).catch(() => {}).then(() => { if (state.slow) pingSoon(); });
  }, 10000);
};

// dot-path injection with append semantics: fileField "feed.photos" merges the freshly
// uploaded paths into any paths that were already uploaded before the signal dropped
const inject = (entry, paths) => {
  if (!entry.fileField) return entry.body;
  const body = JSON.parse(JSON.stringify(entry.body || {}));
  const parts = entry.fileField.split(".");
  let cur = body;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]] = cur[parts[i]] || {};
  const last = parts[parts.length - 1];
  cur[last] = [...(Array.isArray(cur[last]) ? cur[last] : []), ...paths];
  return body;
};

// the copy of a queued request still waiting on this phone (or null)
export const queuedEntry = async (id) => {
  try { return (await idbAll()).find((e) => e.id === id) || null; } catch { return null; }
};

// entries the user removed (e.g. a waiting photo deleted from the note) — even one mid-sync stops
const cancelled = new Set();
export const dequeue = async (id) => {
  cancelled.add(id);
  try {
    await idbDel(id);
    state.pending = (await idbAll()).length;
    emit();
  } catch {}
};

const replay = async (entry) => {
  // each upload is saved back into the entry, so a sync that fails halfway never re-uploads
  // (and never trips the upload rate limit) on the next try
  while (entry.files?.length) {
    if (cancelled.has(entry.id)) return;
    const f = entry.files[0];
    const file = new File([f.blob], f.name || "photo.jpg", { type: f.type || "image/jpeg" });
    const data = await uploadMedia(file, null, { stop: () => cancelled.has(entry.id) });
    entry.uploaded = [...(entry.uploaded || []), data.path];
    entry.files = entry.files.slice(1);
    if (!cancelled.has(entry.id)) await idbPut(entry);
  }
  if (cancelled.has(entry.id)) return;
  const paths = entry.uploaded || [];
  if (entry.kind === "note-photo") {
    // append-only: adds the uploaded photos without rewriting the rest of the note
    await api.post(`/bookings/${entry.bookingId}/notes/photos`, { paths });
    return;
  }
  const body = inject(entry, paths);
  await api.request({ method: entry.method || "post", url: entry.endpoint, data: body });
};

// ---------- drain ----------
let draining = false;
let drainTimer = null;
let drainRun = null;

// force: try even when the phone says it's offline (its flag can lag behind a returning signal)
export function drain(force = false) {
  if (drainRun) return drainRun;
  if (!force && !navigator.onLine) return Promise.resolve();
  drainRun = drainOnce().finally(() => { drainRun = null; });
  return drainRun;
}

// "Sync now": send everything waiting right away → synced | offline | slow | signin | waiting
export const syncNow = async () => {
  await drain(true);
  if (!state.pending) return "synced";
  if (state.authBlocked) return "signin";
  if (!state.online) return "offline";
  return state.slow ? "slow" : "waiting";
};

async function drainOnce() {
  if (draining) return;
  draining = true;
  state.draining = true;
  emit();
  try {
    const entries = (await idbAll()).sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
    for (const entry of entries) {
      try {
        await replay(entry);
        await idbDel(entry.id);
        clearClockFlag(entry);
        if (cancelled.delete(entry.id)) continue; // removed by the user — nothing to report
        window.dispatchEvent(new CustomEvent("shs-outbox-synced", { detail: entry }));
      } catch (err) {
        if (cancelled.delete(entry.id)) continue; // removed by the user mid-sync — not a signal problem
        if (err?.response?.status === 401) {
          state.authBlocked = true; // session expired — keep everything queued until sign-in
          return;
        }
        if (isNetworkError(err)) {
          markNoResponse(err);
          return; // no answer (no signal or slow server) — stop, keep order, try again later
        }
        const status = err?.response?.status;
        if ((entry.kind === "clock-in" && status === 409) || (entry.kind === "clock-out" && status === 404)) {
          // already done on the server (an earlier copy got through, or someone clocked you out) —
          // treat as synced rather than flashing "No active clock-in found."
          await idbDel(entry.id);
          clearClockFlag(entry);
          window.dispatchEvent(new CustomEvent("shs-outbox-synced", { detail: entry }));
          continue;
        }
        if ((status === 429 || status >= 500) && (entry.attempts || 0) < 5) {
          // busy or hiccuping server — keep it (and the order) and try again on the next sync
          entry.attempts = (entry.attempts || 0) + 1;
          await idbPut(entry);
          return;
        }
        // refused for good — drop rather than retry forever, but tell the user
        await idbDel(entry.id);
        clearClockFlag(entry);
        window.dispatchEvent(
          new CustomEvent("shs-outbox-failed", { detail: { entry, status } })
        );
      }
    }
    state.online = true;
    state.authBlocked = false;
  } finally {
    draining = false;
    state.draining = false;
    try {
      state.pending = (await idbAll()).length;
    } catch {}
    emit();
  }
}

function drainSoon() {
  if (drainTimer) return;
  drainTimer = setTimeout(() => {
    drainTimer = null;
    drain();
  }, 400);
}

// ---------- enqueue ----------
export const enqueue = async (entry) => {
  // entry: {kind, endpoint, method, body, files?: [{blob,name,type}], fileField?, bookingId?, id?, label}
  const full = { id: entry.id || crypto.randomUUID(), createdAt: new Date().toISOString(), ...entry };
  try {
    await idbPut(full);
  } catch {
    return null; // IndexedDB unavailable (private mode edge) — caller falls back to a plain error
  }
  state.pending = (await idbAll()).length;
  emit();
  drainSoon();
  return full;
};

// ---------- connectivity tracking ----------
window.addEventListener("online", () => {
  state.online = true;
  emit();
  drain();
});
window.addEventListener("offline", () => {
  state.online = false;
  emit();
});

// a request that gets no answer flips us into no-signal (or slow-server) mode; any answer flips
// back + resumes sync
api.interceptors.request.use((config) => {
  config.__startedAt = Date.now();
  return config;
});
api.interceptors.response.use(
  (resp) => {
    lastOkAt = Date.now();
    const slowOver = state.slow && lastOkAt - slowSince >= SLOW_MIN_MS;
    if (!state.online || slowOver) {
      state.online = true;
      if (slowOver) state.slow = false;
      emit();
    }
    if (state.pending > 0) drainSoon();
    return resp;
  },
  (err) => {
    if (err?.response) lastOkAt = Date.now(); // the server answered (even with an error) — signal is fine
    else if (err?.code !== "ERR_CANCELED" && !(err?.config?.__ping && isTimeout(err))) markNoResponse(err);
    return Promise.reject(err);
  }
);

const heartbeat = setInterval(() => {
  if (navigator.onLine && state.pending > 0) drain();
}, 30000);
// hot-reload tidy: don't accumulate parallel heartbeats during development
if (import.meta.hot) import.meta.hot.dispose(() => clearInterval(heartbeat));

// surface queued counts at boot (app opened while offline)
(async () => {
  try {
    state.pending = (await idbAll()).length;
    if (state.pending > 0) emit();
  } catch {}
})();
